/**
 * @file InputComponent.tsx
 * @description
 * -----------------------------------------------------
 * Componente de input
 * -----------------------------------------------------
 * @version 0.0.1a
 * @created 2024-12-19
 * @modified 2025-07-02
 * @author DLTCode
 * @author Nacho Moya
 * @author Ismael Benabdellah
 * @copyright © DLTCode
 * @license Private
 * */

"use client";

import { useState, forwardRef, useMemo, useCallback } from "react";
import { useTranslations } from "next-intl";

import clsx from "clsx";
import { InputComponentProps } from "@/types/ui";

import { AlertTriangleIcon, CalendarIcon, Eye, EyeOff } from "lucide-react";

const InputComponent = forwardRef<HTMLInputElement, InputComponentProps>(
  (
    {
      id,
      label,
      accept,
      className,
      placeholder,
      isRequired,
      type = "text",
      name,
      value,
      onFocus,
      onBlur,
      onChange,
      onKeyDown,
      error,
      readonly,
      maxLength,
      minLength,
      multiple = false,
      min,
      max,
      autoComplete,
      hiddenLabel = false,
      staticDisplay = false,
      ...props
    },
    ref
  ) => {
    // Valor seguro para inputs controlados
    const safeValue = useMemo(
      () => (type !== "file" ? value : undefined),
      [type, value]
    );
    const isPassword = useMemo(() => type === "password", [type]);

    const button = useTranslations("Buttons");

    const [showPassword, setShowPassword] = useState(false);

    const handleTogglePasswordVisibility = useCallback(() => {
      setShowPassword((prev) => !prev);
    }, []);

    if (staticDisplay) {
      // Asociación programatica etiqueta↔valor para lectores de pantalla.
      const labelId = `${id}-label`;
      return (
        <article className={clsx("modal__details__field", className)}>
          {!hiddenLabel && (
            <span
              id={labelId}
              className="label"
            >
              {label || placeholder}
            </span>
          )}
          <span
            className={clsx(
              "modal__details__value",
              readonly && "modal__details__value--readonly"
            )}
          >
            {value ?? "-"}
          </span>
        </article>
      );
    }

    return (
      <article className={clsx("input__group", className)}>
        {!hiddenLabel && (
          <label
            className={clsx(
              "label",
              !label && "sr-only"
            )}
            htmlFor={id}
          >
            {label || placeholder}
            {isRequired && (
              <span className="label--required" aria-hidden="true">
                *
              </span>
            )}
          </label>
        )}

        <section
          className={clsx(
            "input",
            className,
            error && "input--error",
            readonly && "input--disabled"
          )}
        >
          <input
            ref={ref}
            id={id}
            name={name}
            type={
              isPassword && showPassword
                ? "text"
                : type === "date" && !value
                  ? "text"
                  : type
            }
            value={safeValue}
            placeholder={placeholder}
            onChange={onChange}
            onFocus={
              type === "date" ? (e) => (e.target.type = "date") : onFocus
            }
            onBlur={
              type === "date" && !value
                ? (e) => (e.target.type = "text")
                : onBlur
            }
            min={min}
            max={
              max ||
              (type === "date"
                ? new Date().toISOString().split("T")[0]
                : undefined)
            }
            onKeyDown={onKeyDown}
            readOnly={readonly}
            maxLength={maxLength || undefined}
            minLength={minLength || undefined}
            accept={accept}
            multiple={multiple}
            autoComplete={autoComplete}
            aria-invalid={error ? "true" : undefined}
            aria-describedby={error ? `${id}-error` : undefined}
            aria-required={isRequired ? "true" : undefined}
            {...props}
          />
          {isPassword && (
            <button
              type="button"
              className="show-password"
              onClick={handleTogglePasswordVisibility}
              aria-label={
                showPassword
                  ? button("buttonHidePassword")
                  : button("buttonShowPassword")
              }
            >
              {showPassword ? (
                <EyeOff aria-hidden="true" />
              ) : (
                <Eye aria-hidden="true" />
              )}
            </button>
          )}

          {type === "date" && (
            <button
              type="button"
              className="show-datepicker"
              aria-hidden="true"
              tabIndex={-1}
              onClick={(e) => {
                const input = e.currentTarget
                  .previousSibling as HTMLInputElement;
                input.type = "date";
                input.focus();
                input.showPicker?.(); // Para navegadores que soportan showPicker
              }}
            >
              <CalendarIcon size={18} aria-hidden="true" />
            </button>
          )}
        </section>

        {error && (
          <article className="error__message">
            <AlertTriangleIcon
              size={16}
              className="error__icon"
              aria-hidden="true"
            />
            <span id={`${id}-error`} className="label--error" role="alert">
              {error}
            </span>
          </article>
        )}
      </article>
    );
  }
);

InputComponent.displayName = "InputComponent";
export default InputComponent;
